import React, { useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom/cjs/react-router-dom";
import { useDispatch } from "react-redux";
import ProjectHeader from "../../_shared/ProjectHeader/ProjectHeader";
import {
  STRATEGY_CANVAS_BRIEF,
  STRATEGY_CANVAS_REVIEW,
} from "../../../../data/RouteUrls";
import { InfoIcon } from "../StrategyCanvasHelpers";
import { HeaderContext } from "../../../../HeaderContext";
import { breadCrumbsArray } from "../../../../data/breadCrumbs";
import { setDynamicBreadcrumbs } from "../../_shared/_redux/ProjectActions";
import useStrategyCanvasStore, {
  useStrategyCanvasStoreSelector,
} from "../_redux/StrategyCanvasStore";
import useStrategyCanvasReviewStore, {
  useStrategyCanvasReviewStoreSelector,
} from "./_redux/StrategyCanvasReviewStore";
import StrategyCanvasReviewMode from "./ReviewMode/StrategyCanvasReviewMode";
import StrategyCanvasCanvasMode from "./CanvasMode/StrategyCanvasCanvasMode";
import ReviewInfoModal from "./ReviewInfoModal/ReviewInfoModal";
import "./StrategyCanvasReview.scss";

export const MODE_REVIEW = "review";
export const MODE_CANVAS = "canvas";

// Persisted the same way as other simple UI flags in this app (see
// CoachPanel.js's "isSidebarOpen") - marks that this coach has already seen
// the "how review works" explainer once, so it only auto-opens the very
// first time any review is opened, not on every visit.
const REVIEW_INFO_SEEN_KEY = "scReviewInfoSeen";

const selectClients = (state) => state.clients;
const selectReviewByUniqueId = (state) => state.reviewByUniqueId;

const StrategyCanvasReview = () => {
  const history = useHistory();
  const { briefId } = useParams();
  const dispatch = useDispatch();
  const [mode, setMode] = useState(MODE_REVIEW);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const clients = useStrategyCanvasStoreSelector(selectClients);
  const client = (clients || []).find((sme) => sme.briefId === briefId);
  const companyName = client?.name || "";

  const reviewByUniqueId = useStrategyCanvasReviewStoreSelector(selectReviewByUniqueId);
  const review = reviewByUniqueId[briefId];
  const reviewedCount = review?.reviewed_count ?? 0;
  const totalBlocks = review?.total_blocks ?? null;

  const HeaderMetaData = useContext(HeaderContext);
  useEffect(() => {
    document.title = "Art of Scale | Strategy Canvas Review";
    HeaderMetaData.setHeaderMetadata({ headerTitle: "Strategy Canvas" });
    if (!useStrategyCanvasStore.getState().clients) {
      useStrategyCanvasStore.getState().fetchDashboard();
    }
    // Section 5.1 - safe/idempotent to call again here too, in case the
    // coach lands on this screen directly (bookmark/back-forward) rather
    // than via the "Begin canvas review" button on the brief page.
    useStrategyCanvasStore.getState().enterReview(briefId);
    useStrategyCanvasReviewStore.getState().fetchReview(briefId);
    // eslint-disable-next-line
  }, [briefId]);

  useEffect(() => {
    dispatch(
      setDynamicBreadcrumbs(STRATEGY_CANVAS_REVIEW, [
        breadCrumbsArray[STRATEGY_CANVAS_REVIEW][0],
        { title: companyName, url: STRATEGY_CANVAS_BRIEF.replace(":briefId", briefId) },
        breadCrumbsArray[STRATEGY_CANVAS_REVIEW][2],
      ]),
    );
  }, [companyName, briefId]);

  // First-time onboarding - pop the "how review works" explainer open on its
  // own the very first time a coach ever reaches Review Mode, instead of
  // relying on them noticing the info affordance in the header.
  useEffect(() => {
    if (!localStorage.getItem(REVIEW_INFO_SEEN_KEY)) {
      setShowInfoModal(true);
      localStorage.setItem(REVIEW_INFO_SEEN_KEY, "true");
    }
    // eslint-disable-next-line
  }, []);

  return (
    <div>
      <ProjectHeader
        handleNavigation={() =>
          history.push(STRATEGY_CANVAS_BRIEF.replace(":briefId", briefId))
        }
        showBack
      />
      <div id="strategy-canvas-review">
        <div className="scr-header">
          <div className="scr-title">Strategy Canvas</div>

          <div className="scr-header-right">
            <button
              type="button"
              className="scr-info-icon"
              onClick={() => setShowInfoModal(true)}
            >
              <InfoIcon size={14} />
              <span>How review works</span>
            </button>

            {totalBlocks !== null ? (
              <div className="scr-reviewed">
                Reviewed:<strong> {reviewedCount} of {totalBlocks}</strong>
              </div>
            ) : (
              <span className="scr-reviewed-skel" />
            )}

            <div className="scr-mode-toggle">
              <button
                className={`scr-mode-btn ${mode === MODE_REVIEW ? "active" : ""}`}
                onClick={() => setMode(MODE_REVIEW)}
              >
                Review Mode
              </button>
              <button
                className={`scr-mode-btn ${mode === MODE_CANVAS ? "active" : ""}`}
                onClick={() => setMode(MODE_CANVAS)}
              >
                Canvas Mode
              </button>
            </div>
          </div>
        </div>

        <div className="scr-divider" />

        {mode === MODE_REVIEW ? (
          <StrategyCanvasReviewMode briefId={briefId} />
        ) : (
          <StrategyCanvasCanvasMode briefId={briefId} companyName={companyName} />
        )}
      </div>

      {showInfoModal && (
        <ReviewInfoModal onClose={() => setShowInfoModal(false)} />
      )}
    </div>
  );
};

export default StrategyCanvasReview;
